import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AssumptionDefinition, InputsSnapshot } from "@/types/excel/types";

type Props = {
  disabled?: boolean;
  mapping: AssumptionDefinition[];
  initialValues: InputsSnapshot;
  onApply: (next: InputsSnapshot) => void | Promise<void>;
  onExport: () => void | Promise<void>;
};

function schemaFor(mapping: AssumptionDefinition[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const def of mapping) {
    if (def.format === "text") {
      shape[def.key] = z
        .string()
        .max(200, "Too long")
        .transform((v) => v.trim())
        .refine((v) => v.length > 0, "Must be text");
    } else {
      let s = z.number();
      s = s.refine((val) => !isNaN(val), "Must be a number");

      if (typeof def.min === "number") {
        s = s.refine((val) => val >= def.min!, `Minimum is ${def.min}`);
      }
      if (typeof def.max === "number") {
        s = s.refine((val) => val <= def.max!, `Maximum is ${def.max}`);
      }
      shape[def.key] = s;
    }
  }
  return z.object(shape);
}

export function AssumptionsEditor({
  disabled,
  mapping,
  initialValues,
  onApply,
  onExport,
}: Props) {
  const bySection = useMemo(() => {
    const m = new Map<string, AssumptionDefinition[]>();
    for (const def of mapping) {
      const arr = m.get(def.section) ?? [];
      arr.push(def);
      m.set(def.section, arr);
    }
    return Array.from(m.entries());
  }, [mapping]);

  const [values, setValues] = useState<InputsSnapshot>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues(initialValues ?? {});
    setErrors({});
  }, [initialValues]);

  const validate = () => {
    const s = schemaFor(mapping);
    const result = s.safeParse(values);
    if (result.success) {
      setErrors({});
      return true;
    }
    const nextErrors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const k = String(issue.path?.[0] ?? "");
      nextErrors[k] = issue.message;
    }
    setErrors(nextErrors);
    return false;
  };

  return (
    <div className="mt-4 grid gap-6">
      {!disabled ? null : (
        <p className="text-sm text-muted-foreground">
          Upload a workbook to enable editing.
        </p>
      )}

      <div className="grid gap-6">
        {bySection.map(([section, defs]) => (
          <section key={section} className="grid gap-3">
            <h2 className="text-sm font-semibold tracking-wide text-foreground">
              {section}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {defs.map((def) => {
                const v = values[def.key];
                const suffix = def.format === "percent" ? "%" : "";
                const isText = def.format === "text";

                return (
                  <div key={def.key} className="grid gap-1.5">
                    <label className="text-sm font-medium" htmlFor={def.key}>
                      {def.label}
                    </label>
                    <div className="relative">
                      <Input
                        id={def.key}
                        inputMode={isText ? undefined : "decimal"}
                        disabled={disabled || busy}
                        value={
                          typeof v === "number" || typeof v === "string"
                            ? String(v)
                            : ""
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (isText) {
                            setValues((prev) => ({ ...prev, [def.key]: raw }));
                            return;
                          }
                          const n = raw.trim() === "" ? NaN : Number(raw);
                          setValues((prev) => ({ ...prev, [def.key]: n }));
                        }}
                        className={suffix ? "pr-10" : undefined}
                      />
                      {suffix ? (
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {suffix}
                        </span>
                      ) : null}
                    </div>
                    {errors[def.key] ? (
                      <p className="text-sm font-medium text-destructive">
                        {errors[def.key]}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {def.sheet}!{def.cell}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-muted-foreground">
          Tip: values are written to the workbook, and Excel will recalculate
          when opened.
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || busy}
            onClick={async () => {
              if (!validate()) return;
              setBusy(true);
              try {
                await onApply(values);
              } finally {
                setBusy(false);
              }
            }}
          >
            Apply to workbook
          </Button>
          <Button
            className="bg-primary-blue"
            type="button"
            disabled={disabled || busy}
            onClick={async () => {
              if (!validate()) return;
              setBusy(true);
              try {
                await onApply(values);
                await onExport();
              } finally {
                setBusy(false);
              }
            }}
          >
            Download updated Excel
          </Button>
        </div>
      </div>
    </div>
  );
}
